import { IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonChip, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonLoading, IonNavLink, IonNote, IonPage, IonTitle, IonToolbar, useIonAlert, useIonModal } from "@ionic/react"
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { useTranslation } from "react-i18next";
import { ebioroApi, useTopupEstimatePricesMutation } from "../../services/api/ebioro/ebioro";
import { useAppDispatch, useAppSelector } from "../../services/redux/hook";
import { TopUpPriceObject, TopUpProduct, Asset, getTopUpPromotionParameters, TopUpPromotionDescription, TopUpContact, TopUpProvider } from "../../types/types";
import * as Sentry from '@sentry/react';
import ReactMarkdown from 'react-markdown';
import './Topup.css'
import { arrowBackOutline} from "ionicons/icons";
import { setTopUpPricing } from "../../services/redux/slices/topup_pricing/topup_pricing";
import ToUpPromotionInfoPage from "./ToUpPromotionInfoPage";
import TopupReview from "./TopupReview";
import { Provider } from "react-redux";
import { store } from "../../services/redux/store";

interface PriceEstimationObject {
    SendValue: number;
    ReceiveValue: number;
    SkuCode: string;
    BatchItemRef: string;
}

// Constants for better maintainability
const CUBACEL_SKU_CODE = 'CU_CU_TopUp';
const CUBACEL_RECEIVE_VALUES = [250, 375, 536, 750, 1000, 1250];
const SEND_VALUE_PRICE_INCREASES = [2, 3, 4, 4.5, 5, 6, 7.5];
const MODAL_PRESENTATION_DELAY = 100;

const Page: React.FC = () => {
    const nav = document.querySelector("ion-nav");
    const pageRef = useRef<HTMLElement>(null);

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [showLoading, setShowLoading] = useState(false);
    const [prices, setPrices] = useState<TopUpPriceObject[]>([]);
    const [promotionDescription, setPromotionDescription] = useState<TopUpPromotionDescription[]>([]);
    const [estimatePrices] = useTopupEstimatePricesMutation();
    const [present] = useIonAlert();

    // Redux selectors
    const language = useAppSelector((state) => state.language);
    const topUpContact: TopUpContact = useAppSelector((state) => state.topUpContact);
    const topUpProducts: any = useAppSelector((state) => state.topUpProducts);
    const selectedTopProvider: TopUpProvider = useAppSelector((state) => state.selectedTopUpProvider);
    const quotes = useAppSelector((state) => state.quotes.quotes);
    const supportedAssets: Asset[] = useAppSelector((state) => state.supportedAssets.supportedAssets);

    // Memoized calculations
    const usdcToUSD: number = useMemo(() => quotes["usd-coin"]?.usd || 1, [quotes]);
    const usdcToEUR: number = useMemo(() => quotes["usd-coin"]?.eur || 1, [quotes]);

    const supportedTopUpAsset = useMemo(() => 
        supportedAssets.filter((asset) => asset.topups_enabled)[0],
        [supportedAssets]
    );

    const selectedProviderProducts = useMemo(() => {
        return topUpProducts[selectedTopProvider?.provider_code] || [];
    }, [selectedTopProvider, topUpProducts]);

    // Modal setup
    const [presentPromotionInfo, dismissPromotionInfo] = useIonModal(
        <ToUpPromotionInfoPage 
            info={promotionDescription} 
            closeAction={() => dismissPromotionInfo()} 
        />
    );

    // Helper function for currency conversion
    const calculateUsdcPrice = useCallback((sendValue: number, sendCurrencyIso: string): number | undefined => {
        if (sendCurrencyIso === "USD") {
            return sendValue * usdcToUSD;
        }
        if (sendCurrencyIso === "EUR") {
            return sendValue / usdcToEUR;
        }
        return undefined;
    }, [usdcToUSD, usdcToEUR]);

    const presentErrorAlert = useCallback((error: string) => {
        present({
            header: "Error",
            message: error,
            buttons: [
                {
                    text: "Close",
                    role: "cancel",
                    handler: () => {
                        return;
                    },
                },
            ],
        });
    }, [present]);

    // Helper function to create price object
    const createPriceObject = useCallback((
        product: TopUpProduct,
        price: any,
        promotions: any[],
        validPromotion: boolean = true
    ): TopUpPriceObject => {
        return {
            valid_promotion: validPromotion,
            Product: product,
            Promotions: promotions,
            Price: price,
            UsdcDisplayPrice: calculateUsdcPrice(price.SendValue, price.SendCurrencyIso) || 0,
            UsdcDisplayCustomerFee: calculateUsdcPrice(price.CustomerFee, price.SendCurrencyIso) || 0,
        };
    }, [calculateUsdcPrice]);

    // Process fixed denomination products
    const processFixedDenominationProduct = useCallback(async (
        product: TopUpProduct,
        promotions: any[]
    ): Promise<TopUpPriceObject> => {
        return createPriceObject(product, product.maximum, promotions);
    }, [createPriceObject]);

    // Process Cubacel specific products
    const processCubacelProduct = useCallback(async (
        product: TopUpProduct,
        promotions: any[]
    ): Promise<TopUpPriceObject[]> => {
        const receiveValueArray = CUBACEL_RECEIVE_VALUES.map((value, index) => ({
            SendValue: 0,
            ReceiveValue: value,
            SkuCode: product.sku_code,
            BatchItemRef: (index + 1).toString()
        }));

        try {
            const response = await estimatePrices(receiveValueArray).unwrap();
            const priceObjects: TopUpPriceObject[] = [];

            response.forEach((item, index) => {
                if (!item.ErrorCodes.length || item.ErrorCodes[0].Code === "NearestMatch") {
                    const validPromotion = item.Price.ReceiveValue !== receiveValueArray[index].ReceiveValue;
                    priceObjects.push(createPriceObject(product, item.Price, promotions, validPromotion));
                }
            });

            return priceObjects;
        } catch (error) {
            Sentry.captureException(error);
            throw new Error(t('application.uncaught.ERROR') as string || 'An error occurred');
        }
    }, [estimatePrices, createPriceObject, t]);

    // Process variable range products
    const processVariableRangeProduct = useCallback(async (
        product: TopUpProduct,
        promotions: any[]
    ): Promise<TopUpPriceObject[]> => {
        const sendValueArray: PriceEstimationObject[] = [];
        let sendValue = product.maximum.SendValue;
        let batchItemCounter = 0;

        while (sendValue >= product.minimum.SendValue) {
            const priceObject: PriceEstimationObject = {
                SendValue: sendValue,
                ReceiveValue: 0,
                SkuCode: product.sku_code,
                BatchItemRef: batchItemCounter.toString()
            };

            sendValue -= SEND_VALUE_PRICE_INCREASES[Math.floor(Math.random() * SEND_VALUE_PRICE_INCREASES.length)];

            if (sendValue >= product.minimum.SendValue) {
                sendValueArray.push(priceObject);
            }

            batchItemCounter += 1;
        }

        try {
            const response = await estimatePrices(sendValueArray).unwrap();
            return response.map(item => 
                createPriceObject(product, item.Price, promotions)
            );
        } catch (error) {
            Sentry.captureException(error);
            throw new Error(t('application.uncaught.ERROR') as string || 'An error occurred');
        }
    }, [estimatePrices, createPriceObject, t]);

    const calculatePrices = useCallback(async (products: TopUpProduct[]) => {
        if (!products.length) {
            setShowLoading(false);
            return;
        }

        setShowLoading(true);
        const pricesToAdd: TopUpPriceObject[] = [];

        try {
            // Process all products concurrently for better performance
            const productPromises = products.map(async (product) => {
                try {
                    // Get promotions for this product
                    const params: getTopUpPromotionParameters = {
                        provider_code: product.provider_code,
                        currency_iso: product.maximum.ReceiveCurrencyIso
                    };

                    const promotions = await dispatch(
                        ebioroApi.endpoints.getToUpPromotions.initiate(params)
                    ).unwrap();

                    const priceRatio = product.maximum.SendValue / product.minimum.SendValue;

                    // Fixed denomination (min = max)
                    if (priceRatio === 1) {
                        return [await processFixedDenominationProduct(product, promotions)];
                    }

                    // Variable range products
                    if (priceRatio > 1) {
                        if (product.sku_code === CUBACEL_SKU_CODE) {
                            return await processCubacelProduct(product, promotions);
                        } else {
                            return await processVariableRangeProduct(product, promotions);
                        }
                    }

                    return [];
                } catch (error) {
                    console.error(`Error processing product ${product.sku_code}:`, error);
                    Sentry.captureException(error);
                    return [];
                }
            });

            const results = await Promise.all(productPromises);
            const flattenedPrices = results.flat();
            
            setPrices(flattenedPrices);
        } catch (error) {
            console.error('Error calculating prices:', error);
            Sentry.captureException(error);
            presentErrorAlert(t('application.uncaught.ERROR'));
        } finally {
            setShowLoading(false);
        }
    }, [
        dispatch,
        processFixedDenominationProduct,
        processCubacelProduct,
        processVariableRangeProduct,
        presentErrorAlert,
        t
    ]);


    const goToTopupReviewPage = useCallback((priceObject: TopUpPriceObject) => {
        dispatch(setTopUpPricing(priceObject));
        nav?.push(() => <TopupReview/>)
    }, [dispatch, nav]);

    // Helper function to get localized descriptions
    const getLocalizedDescriptions = useCallback((descriptions: any[], fallbackLanguage = 'en') => {
        let localizedDescriptions = descriptions.filter(desc => desc.language_code === language);
        
        if (!localizedDescriptions.length) {
            localizedDescriptions = descriptions.filter(desc => desc.language_code === fallbackLanguage);
        }
        
        return localizedDescriptions;
    }, [language]);

    const displayPromotionPage = useCallback((info: TopUpPromotionDescription[]) => {
        setPromotionDescription(info);
        presentPromotionInfo({
            presentingElement: pageRef.current || undefined
        })
    }, [presentPromotionInfo]);

    useEffect(() => {
        const init = async () => {
            await calculatePrices(selectedProviderProducts);
        }
        init()
    }, [selectedProviderProducts, calculatePrices])


    return (
        <IonPage ref={pageRef}>

            <IonHeader className="ion-no-border">

                <IonToolbar style={{ marginTop: "0%" }}>
                    <IonNavLink routerDirection={'back'}>
                        <IonButtons slot='start'>
                            <IonButton color={'primary'}>
                                <IonIcon slot='start' icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>
                    </IonNavLink>
                    <IonTitle> {t('topups_products.page.HEADER') as string}</IonTitle>

                </IonToolbar>

            </IonHeader>

            <IonContent>

                <IonLoading isOpen={showLoading} />
                
                    {prices.map((item, index) => {
                        const promotionDescriptions = item.Promotions.length 
                            ? getLocalizedDescriptions(item.Promotions[0].descriptions)
                            : [];

                        const productDescriptions = getLocalizedDescriptions(item.Product.descriptions);

                        const hasValidPromotion = promotionDescriptions.length > 0 && item.valid_promotion;
                        const isFixedDenomination = item.Product.maximum.SendValue === item.Product.minimum.SendValue;

                        return (
                            <IonCard key={index}>
                                <IonCardHeader color='primary'>
                                    <IonCardTitle>
                                        {/* Promotion chip */}
                                        {hasValidPromotion && (
                                            <IonItem 
                                                lines='none' 
                                                color='primary'
                                                onClick={() => displayPromotionPage(promotionDescriptions)}
                                            >
                                                <IonChip 
                                                    slot="end" 
                                                    outline={true} 
                                                    style={{ textDecorationLine: "underline" }}
                                                >
                                                    Promo
                                                </IonChip>
                                            </IonItem>
                                        )}

                                        {/* Price display */}
                                        <IonItem lines="none" color='primary'>
                                            <IonLabel className="ion-text-center">
                                                <h1>
                                                    {item.UsdcDisplayPrice ? item.UsdcDisplayPrice.toFixed(2) : undefined}
                                                    {(item.Price.SendCurrencyIso === "USD" || item.Price.SendCurrencyIso === "EUR") 
                                                        ? ` ${supportedTopUpAsset?.code || 'USDC'}` 
                                                        : " EUR"
                                                    }
                                                </h1>
                                            </IonLabel>
                                        </IonItem>

                                        {/* Promotion headline */}
                                        {hasValidPromotion && promotionDescriptions[0]?.headline && (
                                            <IonItem lines='none' color='primary'>
                                                <IonLabel className="ion-text-center ion-text-wrap">
                                                    <h4>{promotionDescriptions[0].headline}</h4>
                                                </IonLabel>
                                            </IonItem>
                                        )}
                                    </IonCardTitle>
                                </IonCardHeader>

                                <IonCardContent>
                                    {/* Product subtitle - show for fixed denomination (when min == max) */}
                                    {isFixedDenomination && (
                                        <IonCardSubtitle
                                             
                                            className="topup_product_card_subtitle">
                                            {item.Product.default_display_text}
                                        </IonCardSubtitle>
                                    )}

                                    {/* Product description */}
                                    {productDescriptions.length > 0 && productDescriptions[0].description_markdown && (
                                        <div>
                                            <ReactMarkdown children={productDescriptions[0].description_markdown} />
                                        </div>
                                    )}

                                    {/* Receive amount - show for variable denomination (when min != max) */}
                                    {!isFixedDenomination && (
                                        <IonItem lines="none">
                                            <IonLabel>
                                                <IonNote>
                                                    {topUpContact.contact_name ? `${topUpContact.contact_name} ${t('topups_products.page.CONTACT_GETS')}` : `${t('topups_products.page.CONTACT_GETS_EMPTY')}`}
                                                </IonNote>
                                                <h1>
                                                    {item.Price.ReceiveValueExcludingTax} {`${item.Price.ReceiveCurrencyIso}`}
                                                </h1>
                                            </IonLabel>
                                        </IonItem>
                                    )}
                                </IonCardContent>

                                {/* Select button */}
                                <IonItem lines="none" onClick={() => goToTopupReviewPage(item)}>
                                    <IonButton 
                                        slot="end" 
                                        shape="round" 
                                        fill="outline" 
                                        color='primary'
                                    >
                                        {t('topups_products.page.BUTTON-SELECT')}
                                    </IonButton>
                                </IonItem>
                            </IonCard>
                        );
                    })}
            

            </IonContent>

        </IonPage>
    )
}

const TopupProduct: React.FC = () => (
    <Provider store={store}>
        <Page />
    </Provider>
);

export default TopupProduct;